/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Toast from 'react-native-toast-message';
import { View, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import ProgressBar from '@components/global/ProgressBar';
import Image from '@components/global/Image';
import SociollaText from '@components/sociolla/SociollaText';
import TitleSubSection from '@components/global/TitleSubSection';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import Input from '@components/form/Input';
import SelectBox from '@components/form/SelectBox';
import ToggleSwitch from '@components/form/Toggle';
import FormLabel from '@components/form/common/FormLabel';
import FileInput from '@components/form/FileInput';
import * as Layout from '@components/screen/Layout';
import SelectPromotionTable from '@components/form/SelectPromotionTable';
import ModalHalf from '@components/modal/ModalHalf';
import Sections from '@/screens/setting/cms/components/Sections';
import TextSectionForm from '@/screens/setting/cms/components/form/TextSectionForm';
import ImageSectionForm from '@/screens/setting/cms/components/form/ImageSectionForm';
import ProductSectionForm from '@/screens/setting/cms/components/form/ProductSectionForm';
import PdfSectionForm from '@/screens/setting/cms/components/form/PdfSectionForm';
import SelectProductTable from '@components/form/SelectProductTable';
import {
  isWeb,
  isMobileWeb,
  isAndroid,
  isIos,
  fullHeight,
} from '@global/screens';
import { convertPromotion, convertProductMarketType } from '@global/helpers';
import { showPopup } from '@/store/modal';
import { BOOLEAN_STATUS, YES_NO_STATUS } from '@/constants';
import useApi from '@/hooks/useApi';
import { MsCatalog } from '@api/ms-catalog';

const FormValidationSchema = Yup.object().shape({
  title: Yup.string().required('Name is required.'),
  is_active: Yup.boolean(),
  is_slide_banner: Yup.boolean(),
  banner_desktop: Yup.string(),
  banner_mobile: Yup.string(),
  page_type: Yup.string().required('Page Type is required.'),
});

const isMobileDisabled = (values) => {
  return Boolean(values.title && values.page_type);
};

const PAGE_TYPE_OPTIONS = [
  { name: 'Info', value: 'info' },
  { name: 'Guideline', value: 'guideline' },
];

const CMSFormScreen = (props) => {
  const dispatch = useDispatch();
  const pageType = props?.route?.params?.type
    ? props.route.params.type
    : 'info';

  const [page, setPage] = useState(1);
  // Mirrors Formik's values.page_type for use in the mobile "Add Section" ModalHalf below,
  // which is rendered outside the <Formik> render-prop and so can't read `values` directly.
  const [selectedPageType, setSelectedPageType] = useState(pageType);
  const [detailData, setDetailData] = useState({});
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [showAddOption, setShowAddOption] = useState(false);
  const [addPromotion, setAddPromotion] = useState(false);
  const [showForm, setShowForm] = useState({
    text: false,
    image: false,
    product: false,
    pdf: false,
  });
  const [editData, setEditData] = useState(null);
  const [editSectionId, setEditSectionId] = useState(null);
  const [addProduct, setAddProduct] = useState(false);

  const getCMSDetail = useApi(MsCatalog.getCMSDetail);

  const updateInitSections = async (type, payload, isNewForm = false) => {
    if (detailData?._id) {
      await MsCatalog.createCMSSection(detailData._id, type, payload);
      setShowForm((prev) => ({ ...prev, [type]: false }));
      await getSections(detailData._id);
    } else {
      if (!isNewForm) {
        setSections((prevSections) => {
          return prevSections.map((section) => {
            if (section.localId === editSectionId) {
              return { ...section, ...payload };
            }
            return section;
          });
        });
      } else if (type === 'pdf_documents') {
        setSections((prevSections) => {
          const existingIdx = prevSections.findIndex(
            (section) => section.type === 'pdf_documents',
          );
          if (existingIdx > -1) {
            const merged = [...prevSections];
            merged[existingIdx] = {
              ...merged[existingIdx],
              pdf_documents: [
                ...merged[existingIdx].pdf_documents,
                ...payload.pdf_documents,
              ],
            };
            return merged;
          }
          return [...prevSections, { ...payload, type, localId: Date.now() }];
        });
      } else {
        const newPayload = {
          ...payload,
          type,
          localId: Date.now(),
        };
        setSections((prevSections) => [...prevSections, newPayload]);
      }
      setShowForm((prev) => ({ ...prev, [type]: false }));
    }
  };

  const getSections = async (id) => {
    setLoading(true);
    try {
      let result = [];
      try {
        const textImages = await MsCatalog.getCMSSectionDetail(
          id,
          'text-images',
        );
        if (textImages?.data?.data?.length) {
          result = result.concat(textImages.data.data);
        }
      } catch (error) {}
      try {
        const products = await MsCatalog.getCMSSectionDetail(id, 'products');
        if (products?.data?.data?.length) {
          result = result.concat({
            type: 'products',
            products: products.data.data,
          });
        }
      } catch (error) {}
      try {
        const promotions = await MsCatalog.getCMSSectionDetail(
          id,
          'promotions',
        );
        if (promotions?.data?.data?.length) {
          result = result.concat({
            type: 'promotions',
            promotions: convertPromotion(promotions.data.data),
          });
        }
      } catch (error) {}
      try {
        const pdf_documents = await MsCatalog.getCMSSectionDetail(
          id,
          'pdf_documents',
        );
        if (pdf_documents?.data?.data?.length) {
          result = result.concat(pdf_documents.data.data);
        }
      } catch (error) {}
      setSections(result);
      setLoading(false);
    } catch (error) {
      console.log('error', error);
      setSections([]);
      setLoading(false);
    }
  };

  const getDetail = async (id) => {
    const response = await getCMSDetail.request(id);
    if (response?.data?.data && Object.keys(response.data.data).length) {
      const result = response.data.data;
      result.id = result._id;
      getSections(result._id);
      setDetailData(result);
      if (result.page_type) {
        setSelectedPageType(result.page_type);
      }
    }
  };

  const goBack = () => {
    // Edit → back to the detail it was opened from; add → back to the list.
    props.navigation.replace('CMSFormScreen', {});
    if (isEdit) {
      props.navigation.navigate('CMSDetailScreen', props.route.params);
      return;
    }
    props.navigation.navigate('CMSScreen');
  };

  const onShowForm = (type) => {
    setShowAddOption(false);
    if (type == 'product') {
      // const item = sections?.find(s => s.type == 'products' && s.products?.length);
      // const products = item?.products;
      // setEditSectionId(item?._id);
      // setEditData(products);
      setAddProduct(true);
    } else {
      setShowForm({ ...showForm, [type]: true });
    }
  };

  const isMaxSection = useMemo(() => {
    return sections?.length >= 5;
  }, [sections]);

  const onSelectPromotion = async (data) => {
    try {
      dispatch(showPopup({ name: 'isLoading', value: true }));
      const response = await MsCatalog.createCMSSection(
        detailData?._id,
        'promotions',
        {
          promotion_id: data.id,
          name: data.name,
          promotion_type: data.promotion_type,
          description: data.description,
          is_active: data.is_active,
          from_date: data.from_date,
          to_date: data.to_date,
        },
      );

      if (response?.data?.data?._id || response?.data?.success) {
        const new_sections = [...sections];
        if (new_sections.find((a) => a.promotions?.length)) {
          const idx = new_sections.findIndex((a) => a.promotions?.length);
          if (idx > -1) {
            const oldPromo = [...new_sections[idx].promotions];
            oldPromo.push(data);
            new_sections[idx].promotions = convertPromotion(oldPromo);
          } else {
            new_sections.push({
              type: 'promotions',
              promotions: convertPromotion([data]),
            });
          }
        } else {
          new_sections.push({
            type: 'promotions',
            promotions: convertPromotion([data]),
          });
        }
        setSections(new_sections);
        setAddPromotion(false);
        // getDetail(props.route.params.id)
        Toast.show({
          text1: 'Successfully added product',
          type: 'success',
        });
      } else {
        Toast.show({ text1: 'A system error occurred', type: 'error' });
      }
      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (err) {
      console.log('err', err);
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: err?.response?.data?.message || 'A system error occurred',
        type: 'error',
      });
    }
  };

  const onSelectProduct = async (data) => {
    if (!isEdit) {
      dispatch(showPopup({ name: 'isLoading', value: true }));
      const newdata = convertProductMarketType(data);
      const productPayload = {
        type: 'products',
        products: [{ products: newdata }],
      };
      if (sections && sections.length > 0) {
        const oldProductIndex = sections.findIndex(
          (s) => s.type === 'products',
        );
        if (oldProductIndex !== -1) {
          const updatedSections = [...sections];
          updatedSections[oldProductIndex].products.push({ products: newdata });
          setSections(updatedSections);
        } else {
          const updatedSections = [...sections, productPayload];
          setSections(updatedSections);
        }
        dispatch(showPopup({ name: 'isLoading', value: false }));
      } else {
        setSections((prevSections) => [...prevSections, productPayload]);
        dispatch(showPopup({ name: 'isLoading', value: false }));
      }
      setAddProduct(false);
    } else {
      try {
        dispatch(showPopup({ name: 'isLoading', value: true }));
        const response = await MsCatalog.createCMSSection(
          detailData?._id,
          'products',
          {
            product_id: data.id,
            combination_id: data.product_combination_id,
            price: data.price,
          },
        );

        if (response?.data?.data?._id || response?.data?.success) {
          const new_sections = JSON.parse(JSON.stringify(sections));
          const productIdx = new_sections.findIndex(
            (s) => s.type == 'products' && s.products?.length,
          );
          if (productIdx > -1) {
            new_sections[productIdx].products.push(
              convertProductMarketType(data),
            );
            setSections(new_sections);
            setAddProduct(false);
            getSections(detailData?._id);
          }
          Toast.show({
            text1: 'Successfully added product',
            type: 'success',
          });
          getDetail(detailData?._id);
          setAddProduct(false);
        } else {
          Toast.show({ text1: 'A system error occurred', type: 'error' });
        }
        dispatch(showPopup({ name: 'isLoading', value: false }));
      } catch (err) {
        console.log('err', err);
        dispatch(showPopup({ name: 'isLoading', value: false }));
        Toast.show({
          text1: err?.response?.data?.message || 'A system error occurred',
          type: 'error',
        });
      }
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setIsEdit(false);
      setEditSectionId(null);
      setEditData(null);
      setDetailData({});
      setShowForm({
        text: false,
        image: false,
        product: false,
        pdf: false,
      });
      if (props?.route?.params?.id) {
        setIsEdit(true);
        getDetail(props.route.params.id);
      }
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  return (
    <Layout.Container>
      {!isWeb && !isMobileWeb && <ProgressBar page={page} totalPage={2} />}
      {isEdit && getCMSDetail?.loading ? (
        <Layout.Wrapper>
          <Layout.Loader />
        </Layout.Wrapper>
      ) : (isEdit ? detailData && Object.keys(detailData).length : true) ? (
        <Formik
          initialValues={{
            title: isEdit && detailData?.title ? detailData.title : '',
            is_active:
              isEdit && detailData?.is_active ? detailData.is_active : false,
            is_slide_banner:
              isEdit && detailData?.is_slide_banner
                ? detailData.is_slide_banner
                : false,
            banner_desktop:
              isEdit && detailData?.banner_desktop
                ? detailData.banner_desktop
                : '',
            banner_mobile:
              isEdit && detailData?.banner_mobile
                ? detailData.banner_mobile
                : '',
            // Prefer the record's own saved type when editing — route.params.type is only a
            // reliable signal for create-mode now that "+Add" no longer pre-selects a type.
            page_type:
              isEdit && detailData?.page_type ? detailData.page_type : pageType,
          }}
          validationSchema={FormValidationSchema}
          onSubmit={async (payload) => {
            dispatch(showPopup({ name: 'isLoading', value: true }));
            try {
              const finalPayload = {
                title: payload.title,
                description: payload.description,
                is_active: payload.is_active,
                page_type: payload.page_type,
                ...(payload.page_type !== 'guideline'
                  ? {
                      is_slide_banner: payload.is_slide_banner,
                      banner_desktop: payload.banner_desktop,
                      banner_mobile: payload.banner_mobile,
                    }
                  : {}),
              };

              if (!isEdit) {
                const modifiedSections = sections.reduce((idx, section) => {
                  if (section.type === 'products') {
                    section.products.forEach((product) => {
                      idx.push({
                        type: 'products',
                        product_id: product.products.id,
                        combination_id: product.products.product_combination_id,
                        price: product.products.price,
                      });
                    });
                  } else {
                    idx.push(section);
                  }
                  return idx;
                }, []);
                const mixedPayload = {
                  ...finalPayload,
                  sections: modifiedSections,
                };
                const createData = await MsCatalog.createCMS(mixedPayload);
                if (createData?.data?.data) {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'Successfully added data',
                    type: 'success',
                  });
                  props.navigation.navigate('CMSScreen');
                } else {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'A system error occurred',
                    type: 'error',
                  });
                }
              } else {
                const updateData = await MsCatalog.updateCMS(
                  detailData._id,
                  finalPayload,
                );
                if (updateData?.data?.data) {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'Successfully edited data',
                    type: 'success',
                  });
                  props.navigation.navigate('CMSDetailScreen', {
                    id: updateData?.data?.data?._id,
                    name: updateData?.data?.data?.title,
                    titleScreen: updateData?.data?.data?.title,
                    type: payload.page_type,
                  });
                } else {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'A system error occurred',
                    type: 'error',
                  });
                }
              }
              dispatch(showPopup({ name: 'isLoading', value: false }));
            } catch (error) {
              console.log(error);
              dispatch(showPopup({ name: 'isLoading', value: false }));
              Toast.show({
                text1:
                  error?.response?.data?.message || 'A system error occurred',
                type: 'error',
              });
            }
          }}>
          {({
            setFieldValue,
            validateForm,
            handleSubmit,
            errors,
            touched,
            values,
          }) => (
            <>
              {isWeb ? (
                <Layout.Wrapper white={false}>
                  <Layout.Back title="CMS" onCallback={() => goBack()} />
                  <Layout.Title
                    title={`${isEdit ? `Edit` : `Add New`} ${
                      values.page_type == 'guideline'
                        ? 'Guideline'
                        : values.page_type == 'info'
                        ? 'Info'
                        : 'Promo'
                    } Page`}>
                    <View style={styles.row}>
                      <Button
                        type="primary"
                        rounded
                        buttonStyle={{ width: 120 }}
                        title={
                          <SociollaText
                            type="bold"
                            style={{
                              fontSize: 14,
                              letterSpacing: 0.5,
                              color: Colors.white,
                            }}>
                            {isEdit ? 'SAVE' : 'ADD'}
                          </SociollaText>
                        }
                        onPress={handleSubmit}
                      />
                    </View>
                  </Layout.Title>
                </Layout.Wrapper>
              ) : (
                false
              )}
              <Layout.Wrapper rounded hasProgressBar={!isWeb && !isMobileWeb}>
                <ScrollView
                  style={isMobileWeb ? { height: fullHeight - 55 } : {}}>
                  {page == 1 || isWeb || isMobileWeb ? (
                    <Layout.FormWrapper
                      style={{
                        paddingHorizontal: 15,
                        paddingTop: 20,
                        ...(isWeb
                          ? {
                              position: 'relative',
                              zIndex: 2,
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              paddingHorizontal: 20,
                              marginBottom: 15,
                            }
                          : {
                              paddingBottom: !isMobileWeb
                                ? isAndroid
                                  ? 150
                                  : 80
                                : 15,
                            }),
                      }}>
                      <View style={styles.rowForm}>
                        <Layout.FormGroup>
                          <Input
                            label="Title Page"
                            value={values.title}
                            isRequired
                            placeholder="Title Page"
                            hasError={
                              errors.title && touched.title
                                ? errors.title
                                : null
                            }
                            onChangeText={(val) => {
                              setFieldValue('title', val);
                            }}
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup
                          style={isWeb ? { zIndex: 10002 } : {}}>
                          <SelectBox
                            label="Page Type"
                            isDropdown={isWeb}
                            hideSearch
                            hideItemBorder={!isWeb}
                            compactPaddingBottom
                            options={PAGE_TYPE_OPTIONS}
                            value={PAGE_TYPE_OPTIONS.find(
                              (option) => option.value === values.page_type,
                            )}
                            placeholder="Select Page Type"
                            trackBy="value"
                            labelKey="name"
                            isRequired
                            hasError={
                              errors.page_type && touched.page_type
                                ? errors.page_type
                                : null
                            }
                            onChange={(value) => {
                              setFieldValue('page_type', value.value);
                              setSelectedPageType(value.value);
                            }}
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup
                          style={isWeb ? { zIndex: 10001 } : {}}>
                          {isWeb ? (
                            <SelectBox
                              label="Status"
                              isDropdown={isWeb}
                              options={BOOLEAN_STATUS}
                              value={
                                values.is_active
                                  ? BOOLEAN_STATUS[0]
                                  : BOOLEAN_STATUS[1]
                              }
                              placeholder="Select Status"
                              trackBy="value"
                              labelKey="name"
                              isRequired
                              hasError={
                                errors.is_active && touched.is_active
                                  ? errors.is_active
                                  : null
                              }
                              onChange={(value) =>
                                setFieldValue('is_active', value.value)
                              }
                            />
                          ) : (
                            <>
                              <FormLabel label="Status" isRequired />
                              <View style={styles.toggleRow}>
                                <ToggleSwitch
                                  size="small"
                                  value={!!values.is_active}
                                  onValueChange={(val) =>
                                    setFieldValue('is_active', val)
                                  }
                                />
                                <SociollaText style={styles.toggleRowText}>
                                  {values.is_active ? 'Active' : 'In Active'}
                                </SociollaText>
                              </View>
                            </>
                          )}
                        </Layout.FormGroup>
                        {values.page_type !== 'guideline' ? (
                          <>
                            <Layout.FormGroup>
                              <FileInput
                                label="Banner Desktop"
                                value={values.banner_desktop}
                                isImage
                                onChange={(value) => {
                                  setFieldValue('banner_desktop', value);
                                }}
                              />
                            </Layout.FormGroup>
                            <Layout.FormGroup>
                              <FileInput
                                label="Banner Mobile"
                                value={values.banner_mobile}
                                isImage
                                onChange={(value) => {
                                  setFieldValue('banner_mobile', value);
                                }}
                              />
                            </Layout.FormGroup>
                            <Layout.FormGroup
                              style={isWeb ? { zIndex: 10000 } : {}}>
                              <SelectBox
                                label="Enable Slider Banner"
                                options={YES_NO_STATUS}
                                value={
                                  values.is_slide_banner
                                    ? YES_NO_STATUS[0]
                                    : YES_NO_STATUS[1]
                                }
                                placeholder="Select Enable Slide Banner"
                                trackBy="value"
                                labelKey="name"
                                isRequired
                                hasError={
                                  errors.is_slide_banner &&
                                  touched.is_slide_banner
                                    ? errors.is_slide_banner
                                    : null
                                }
                                onChange={(value) =>
                                  setFieldValue('is_slide_banner', value.value)
                                }
                              />
                            </Layout.FormGroup>
                          </>
                        ) : (
                          false
                        )}
                      </View>
                    </Layout.FormWrapper>
                  ) : (
                    false
                  )}
                  {page == 2 || isWeb || isMobileWeb ? (
                    <View
                      style={isWeb ? { position: 'relative', zIndex: 1 } : {}}>
                      {isMobileWeb &&
                      (values.page_type === 'promo' || !isMaxSection) ? (
                        <>
                          <View style={styles.mobileSectionDivider} />
                          <View style={styles.addSectionMobileWrapper}>
                            <Button
                              type="default"
                              rounded
                              buttonStyle={styles.addSectionMobileButton}
                              disabled={loading}
                              title={
                                <View
                                  style={styles.addSectionMobileButtonInner}>
                                  <SociollaText
                                    type="bold"
                                    style={styles.addSectionMobileButtonText}>
                                    {values.page_type === 'promo'
                                      ? 'Add Promotion Rule'
                                      : values.page_type === 'guideline'
                                      ? 'Add PDF Section'
                                      : 'Add Section'}
                                  </SociollaText>
                                  {values.page_type === 'info' ? (
                                    <Image
                                      width={10}
                                      height={6}
                                      svg={true}
                                      style={
                                        showAddOption
                                          ? styles.addSectionMobileArrowUp
                                          : styles.addSectionMobileArrowDown
                                      }
                                      source={require('@assets/img/icons/arrow-down-grey.svg')}
                                    />
                                  ) : (
                                    false
                                  )}
                                </View>
                              }
                              onPress={() =>
                                values.page_type === 'promo'
                                  ? setAddPromotion(true)
                                  : values.page_type === 'guideline'
                                  ? onShowForm('pdf')
                                  : setShowAddOption(!showAddOption)
                              }
                            />
                          </View>
                        </>
                      ) : (
                        false
                      )}
                      {isWeb ? (
                        <TitleSubSection
                          title={
                            values.page_type === 'promo'
                              ? 'PROMOTION RULE'
                              : 'SECTIONS'
                          }
                          disabled={loading}
                          button={
                            values.page_type === 'promo'
                              ? {
                                  label: 'Add Promo Rule',
                                  callback: () => setAddPromotion(true),
                                }
                              : !isMaxSection
                              ? { label: 'Add Section' }
                              : null
                          }
                          style={isWeb ? { zIndex: 1000 } : {}}
                          options={
                            isWeb && values.page_type === 'guideline'
                              ? [
                                  {
                                    label: 'PDF Section',
                                    value: 'pdf',
                                    callback: () => {
                                      onShowForm('pdf');
                                    },
                                  },
                                ]
                              : isWeb && values.page_type === 'info'
                              ? [
                                  {
                                    label: 'Text Section',
                                    value: 'text',
                                    callback: () => {
                                      onShowForm('text');
                                    },
                                  },
                                  {
                                    label: 'Image Section',
                                    value: 'image',
                                    callback: () => {
                                      onShowForm('image');
                                    },
                                  },
                                  {
                                    label: 'Product Section',
                                    value: 'product',
                                    callback: () => {
                                      onShowForm('product');
                                    },
                                  },
                                  {
                                    label: 'PDF Section',
                                    value: 'pdf',
                                    callback: () => {
                                      onShowForm('pdf');
                                    },
                                  },
                                ]
                              : []
                          }
                        />
                      ) : (
                        false
                      )}
                      <Layout.FormWrapper
                        style={{
                          paddingTop: 10,
                          paddingHorizontal: isWeb ? 20 : 0,
                          paddingBottom: isAndroid
                            ? 170
                            : isIos
                            ? 120
                            : isMobileWeb
                            ? 140
                            : 10,
                          ...(isWeb
                            ? {
                                minHeight: 200,
                              }
                            : {}),
                        }}>
                        <Sections
                          isEdit={isEdit}
                          pageType={values.page_type}
                          detail={detailData}
                          sections={sections}
                          loading={loading}
                          onEdit={({ type, data, id, localId }) => {
                            setEditSectionId(localId || id);
                            setShowForm({ ...showForm, [type]: true });
                            setEditData(data);
                          }}
                          onReload={() => getDetail(props.route.params.id)}
                          onSectionsUpdate={(data) => {
                            setSections(data);
                          }}
                        />
                      </Layout.FormWrapper>
                    </View>
                  ) : (
                    false
                  )}
                </ScrollView>
              </Layout.Wrapper>
              {!isWeb ? (
                <Layout.ButtonSticky
                  show={
                    props?.route?.name == 'CMSFormScreen' &&
                    [1, 2].includes(page)
                  }
                  buttons={
                    isMobileWeb
                      ? [
                          {
                            title: isEdit ? 'Save' : 'Add',
                            disabled: !isMobileDisabled(values),
                            onPress: (evt) => handleSubmit(evt),
                          },
                        ]
                      : page == 1
                      ? [
                          {
                            title: isEdit ? 'Next' : 'Save',
                            disabled: !isMobileDisabled(values),
                            onPress: (evt) =>
                              isEdit ? setPage(2) : handleSubmit(evt),
                          },
                        ]
                      : page == 2
                      ? [
                          !isMaxSection && {
                            title:
                              values.page_type === 'promo'
                                ? 'Add Promotion Rule'
                                : 'Add Section',
                            type: 'default',
                            onPress: () =>
                              values.page_type === 'promo'
                                ? setAddPromotion(true)
                                : setShowAddOption(true),
                          },
                          {
                            title: 'Save',
                            onPress: (evt) => {
                              evt.persist();
                              setTimeout(() => {
                                validateForm().then((validate) => {
                                  handleSubmit(evt);
                                  if (
                                    validate &&
                                    Object.keys(validate).length
                                  ) {
                                    setPage(1);
                                  }
                                });
                              }, 100);
                            },
                          },
                        ]
                      : []
                  }
                />
              ) : (
                false
              )}
            </>
          )}
        </Formik>
      ) : (
        false
      )}
      <TextSectionForm
        updateInitBodyText={updateInitSections}
        id={detailData?._id}
        show={showForm.text}
        sectionId={editSectionId}
        editData={editData}
        onClose={() => {
          setEditSectionId(null);
          setEditData(null);
          setShowForm({ ...showForm, text: false });
        }}
        onSubmit={() => {
          setEditSectionId(null);
          setEditData(null);
          setShowForm({ ...showForm, text: false });
          getSections(detailData?._id);
        }}
      />
      <ImageSectionForm
        updateInitImages={updateInitSections}
        id={detailData?._id}
        show={showForm.image}
        sectionId={editSectionId}
        editData={editData}
        onClose={() => {
          setEditSectionId(null);
          setEditData(null);
          setShowForm({ ...showForm, image: false });
        }}
        onSubmit={() => {
          setEditSectionId(null);
          setEditData(null);
          setShowForm({ ...showForm, image: false });
          getSections(detailData?._id);
        }}
      />
      <ProductSectionForm
        id={detailData?._id}
        show={showForm.product}
        sectionId={editSectionId}
        editData={editData}
        onClose={() => {
          setEditSectionId(null);
          setEditData(null);
          setShowForm({ ...showForm, product: false });
        }}
        onSubmit={() => {
          setEditSectionId(null);
          setEditData(null);
          setShowForm({ ...showForm, product: false });
          getSections(detailData?._id);
        }}
        onAdd={() => {
          getSections(detailData?._id);
        }}
      />
      <PdfSectionForm
        updateInitPDF={updateInitSections}
        id={detailData?._id}
        show={showForm.pdf}
        sectionId={editSectionId}
        editData={editData}
        onClose={() => {
          setEditSectionId(null);
          setEditData(null);
          setShowForm({ ...showForm, pdf: false });
        }}
        onSubmit={() => {
          setEditSectionId(null);
          setEditData(null);
          setShowForm({ ...showForm, pdf: false });
          getSections(detailData?._id);
        }}
      />
      <ModalHalf
        title="Add Section"
        visible={!isWeb && showAddOption}
        maxWidth={'100%'}
        spaceFromTop={false}
        onClose={() => setShowAddOption(false)}>
        <View style={styles.addOptions}>
          {selectedPageType !== 'guideline' ? (
            <>
              <TouchableOpacity
                activeOpacity={0.8}
                style={styles.addOptionsItem}
                onPress={() => onShowForm('text')}>
                <SociollaText>Text Section</SociollaText>
              </TouchableOpacity>
              <TouchableOpacity
                activeOpacity={0.8}
                style={styles.addOptionsItem}
                onPress={() => onShowForm('image')}>
                <SociollaText>Image Section</SociollaText>
              </TouchableOpacity>
              <TouchableOpacity
                activeOpacity={0.8}
                style={styles.addOptionsItem}
                onPress={() => onShowForm('product')}>
                <SociollaText>Product Section</SociollaText>
              </TouchableOpacity>
            </>
          ) : (
            false
          )}
          <TouchableOpacity
            activeOpacity={0.8}
            style={styles.addOptionsItem}
            onPress={() => onShowForm('pdf')}>
            <SociollaText>PDF Section</SociollaText>
          </TouchableOpacity>
        </View>
      </ModalHalf>
      <SelectPromotionTable
        show={addPromotion}
        onClose={() => setAddPromotion(false)}
        onCallback={(val) => onSelectPromotion(val)}
      />
      <SelectProductTable
        show={addProduct}
        onClose={() => setAddProduct(false)}
        onCallback={(val) => onSelectProduct(val)}
      />
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  row: { flexDirection: 'row' },
  rowForm: { width: isWeb ? 'calc(50% - 20px)' : '100%' },
  toggleRow: { flexDirection: 'row', alignItems: 'center' },
  toggleRowText: { fontSize: 14, marginLeft: 10, color: Colors.black1 },
  addOptions: {
    width: '100%',
  },
  addOptionsItem: {
    paddingVertical: 13,
    paddingHorizontal: 15,
    borderBottomColor: Colors.grey0,
    borderBottomWidth: 1,
  },
  mobileSectionDivider: {
    height: 12,
    backgroundColor: Colors.grey5,
  },
  addSectionMobileWrapper: {
    paddingHorizontal: 15,
    paddingTop: 20,
    marginBottom: 15,
  },
  addSectionMobileButton: {
    width: 'auto',
    alignSelf: 'flex-start',
    height: 48,
    borderRadius: 8,
    paddingHorizontal: 20,
  },
  addSectionMobileButtonInner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  addSectionMobileButtonText: {
    fontSize: 14,
    letterSpacing: 0.5,
    color: Colors.black,
  },
  addSectionMobileArrowDown: { width: 10, height: 6, marginLeft: 10 },
  addSectionMobileArrowUp: {
    width: 10,
    height: 6,
    marginLeft: 10,
    transform: [{ rotate: '180deg' }],
  },
});

export default CMSFormScreen;
